import React, { useState, useEffect } from 'react';
import { authLogger, LogEntry } from '../utils/logger';

interface DebugLoggerProps {
  autoShow?: boolean;
}

const DebugLogger: React.FC<DebugLoggerProps> = ({ autoShow = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [copied, setCopied] = useState(false);
  const [tab, setTab] = useState<'logs' | 'system'>('logs');

  useEffect(() => {
    const currentLogs = authLogger.getLogs();
    setLogs(currentLogs);

    if (autoShow && currentLogs.some(log => log.level === 'error' || log.level === 'warning')) {
      setIsOpen(true);
    }
  }, [autoShow]);

  const handleCopy = async () => {
    try {
      const report = authLogger.getFullReport();
      await navigator.clipboard.writeText(report);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      const textArea = document.createElement('textarea');
      textArea.value = authLogger.getFullReport();
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Copy failed:', err);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleDownload = () => {
    try {
      const report = authLogger.getFullReport();
      const blob = new Blob([report], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `auth-logs-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleClear = () => {
    if (confirm('Очистить логи?')) {
      authLogger.clearLogs();
      setLogs([]);
    }
  };

  const getLevelColor = (level: LogEntry['level']) => {
    switch (level) {
      case 'error': return 'text-red-400 bg-red-500/10 border-red-500/30';
      case 'warning': return 'text-amber-400 bg-amber-500/10 border-amber-500/30';
      case 'success': return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30';
      default: return 'text-blue-400 bg-blue-500/10 border-blue-500/30';
    }
  };

  const getLevelIcon = (level: LogEntry['level']) => {
    const className = "w-3.5 h-3.5";
    switch (level) {
      case 'error':
        return <svg className={className} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>;
      case 'warning':
        return <svg className={className} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>;
      case 'success':
        return <svg className={className} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>;
      default:
        return <svg className={className} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" /></svg>;
    }
  };

  const systemInfo = authLogger.getSystemInfo();

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed right-4 bottom-4 z-50 flex items-center px-3 py-2 space-x-2 text-xs font-medium text-zinc-400 bg-zinc-900 border border-zinc-800 rounded-lg hover:bg-zinc-800 hover:text-zinc-300 transition-colors"
        title="Открыть логи"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <span>Логи</span>
        {logs.some(log => log.level === 'error' || log.level === 'warning') && (
          <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></span>
        )}
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-center items-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="flex flex-col w-full max-w-lg max-h-[90vh] bg-zinc-900 rounded-2xl border border-zinc-800 overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center px-4 py-3 border-b border-zinc-800">
          <h3 className="text-sm font-medium text-white">Диагностика</h3>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1 text-zinc-500 hover:text-zinc-300 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-zinc-800">
          <button
            onClick={() => setTab('logs')}
            className={`flex-1 px-4 py-2.5 text-xs font-medium transition-colors ${
              tab === 'logs'
                ? 'text-white border-b-2 border-white'
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
          >
            Логи ({logs.length})
          </button>
          <button
            onClick={() => setTab('system')}
            className={`flex-1 px-4 py-2.5 text-xs font-medium transition-colors ${
              tab === 'system'
                ? 'text-white border-b-2 border-white'
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
          >
            Система
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {tab === 'logs' ? (
            <div className="space-y-2">
              {logs.length === 0 ? (
                <div className="py-8 text-center text-zinc-600">
                  <p className="text-sm">Логи отсутствуют</p>
                </div>
              ) : (
                logs.map((log, index) => (
                  <div
                    key={index}
                    className={`p-2.5 rounded-lg border ${getLevelColor(log.level)}`}
                  >
                    <div className="flex items-start space-x-2">
                      <div className="flex-shrink-0 mt-0.5">{getLevelIcon(log.level)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] opacity-60">
                          {new Date(log.timestamp).toLocaleString('ru-RU')}
                        </div>
                        <div className="text-xs font-medium break-words mt-0.5">
                          {log.message}
                        </div>
                        {log.details && (
                          <details className="mt-1.5">
                            <summary className="text-[10px] cursor-pointer opacity-60 hover:opacity-100">
                              Подробности
                            </summary>
                            <pre className="mt-1 p-2 text-[10px] bg-black/30 rounded overflow-x-auto">
                              {JSON.stringify(log.details, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {Object.entries(systemInfo).map(([key, value]) => (
                <div key={key} className="p-2.5 bg-zinc-800/50 rounded-lg">
                  <div className="text-[10px] text-zinc-500 uppercase">{key}</div>
                  <div className="text-xs text-zinc-300 break-all mt-0.5">
                    {typeof value === 'boolean' ? (value ? '✓' : '✗') : String(value)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-2 p-3 border-t border-zinc-800">
          <button
            onClick={handleCopy}
            className="flex-1 flex items-center justify-center px-3 py-2 text-xs font-medium text-zinc-300 bg-zinc-800 rounded-lg hover:bg-zinc-700 transition-colors"
          >
            {copied ? 'Скопировано!' : 'Копировать'}
          </button>
          <button
            onClick={handleDownload}
            className="flex-1 flex items-center justify-center px-3 py-2 text-xs font-medium text-zinc-300 bg-zinc-800 rounded-lg hover:bg-zinc-700 transition-colors"
          >
            Скачать
          </button>
          <button
            onClick={handleClear}
            className="px-3 py-2 text-xs font-medium text-red-400 bg-red-500/10 rounded-lg hover:bg-red-500/20 transition-colors"
          >
            Очистить
          </button>
        </div>
      </div>
    </div>
  );
};

export default DebugLogger;
